import {
  DEVICE_STATUS,
  DEVICE_STATUS_DISPLAY,
  FILTER_KEYS,
  LOCATION_PREFIXES,
  LOCATION_TYPES,
  PAGE_IDENTIFIERS,
  TABLE_FIELDS,
} from "../constants/tableConstants";

// Apply location filter for Cameras tab 
export const applyCameraLocationFilter = (record, selectedOptions) => {
  const expectedIds = {};
  selectedOptions[FILTER_KEYS.LOCATION].forEach((option) => {
    const [name, val] = option.split("_");
    if (name && val) {
      expectedIds[name] = val;
    }
  });

  const tableArea = record?.Location?.area?.tag;
  const tableFloor = record?.Location?.floor?.tag;
  const tableSite = record?.Location?.site?.tag;

  const siteMatches = !expectedIds?.site || (tableSite && tableSite === expectedIds?.site);
  const floorMatches = !expectedIds?.floor || (tableFloor && tableFloor === expectedIds?.floor);
  const areaMatches = !expectedIds?.area || (tableArea && tableArea === expectedIds?.area);
  return siteMatches && floorMatches && areaMatches;
};

// Apply location filter for Companion tab
export const applyCompanionLocationFilter = (record, selectedOptions) => {
  const expectedIds = {};
  selectedOptions[FILTER_KEYS.LOCATION].forEach((option) => {
    const [name, val] = option.split("_");
    if (name && val) {
      expectedIds[name] = val;
    }
  });

  if (record?.SiteIds?.includes(expectedIds?.site)) {
    return true;
  }
  return false;
};

// Apply location filter for Alerts tab
export const applyAlertsLocationFilter = (record, selectedOptions) => {
  const expectedIds = {
    site: null,
    floor: null,
    area: [], // Areas can be multiple
  };

  selectedOptions[FILTER_KEYS.LOCATION]?.forEach((option) => {
    const [name, val] = option.split("_");
    if (name && val) {
      if (name === LOCATION_TYPES.AREA) {
        expectedIds?.area?.push(val); // Collect all areas
      } else {
        expectedIds[name] = val; // Site and floor are single values
      }
    }
  });

  const tableSite = record?.siteId;
  const tableFloor = record?.floorId;
  const tableArea = record?.areaId;

  // Site only filter
  if (
    tableSite === expectedIds?.site &&
    !expectedIds?.floor &&
    expectedIds?.area?.length === 0
  ) {
    return true;
  }
  // Site + Floor filter
  else if (
    tableSite === expectedIds?.site &&
    tableFloor === expectedIds?.floor &&
    expectedIds?.area?.length === 0
  ) {
    return true;
  }
  // Site + Floor + Area(s) filter
  else if (
    tableSite === expectedIds?.site &&
    tableFloor === expectedIds?.floor &&
    expectedIds?.area?.length > 0 &&
    expectedIds?.area?.includes(tableArea)
  ) {
    return true;
  }
  return false;
};

// Apply camera status filter
export const applyCameraStatusFilter = (record, selectedOptions) => {
  const selectedStatuses = selectedOptions[FILTER_KEYS.STATUS];
  if (!selectedStatuses || selectedStatuses.length === 0) {
    return true;
  }

  const rawStatus = (record[FILTER_KEYS.STATUS] || "").toLowerCase();
  const connectivity = (record?.connectivityStatus || "").toLowerCase();
  const calibration = record?.[TABLE_FIELDS.FIELDS]?.[TABLE_FIELDS.CALIBRATION];
  const calibrationStatus = calibration?.status;

  // Check for Offline status first (takes precedence over all other states)
  if (connectivity === DEVICE_STATUS_DISPLAY.OFFLINE.toLowerCase()) {
    return selectedStatuses.includes(DEVICE_STATUS_DISPLAY.OFFLINE);
  }

  // Check for Online status
  if (selectedStatuses.includes(DEVICE_STATUS_DISPLAY.ONLINE)) {
    if (rawStatus === DEVICE_STATUS.READY) {
      return true;
    }
  }

  // Check for Not Calibrated status
  if (selectedStatuses.includes(DEVICE_STATUS_DISPLAY.NOT_CALIBRATED)) {
    if (rawStatus === DEVICE_STATUS.REGISTERED && (calibration === undefined || calibrationStatus == null || calibrationStatus < 0)) {
      return true;
    }
  }

  // Check for Calibrating status
  if (selectedStatuses.includes(DEVICE_STATUS_DISPLAY.CALIBRATING)) {
    if (rawStatus === DEVICE_STATUS.REGISTERED && calibrationStatus != null && calibrationStatus >= 0 && calibrationStatus < 100) {
      return true;
    }
  }

  // Check for Pending status (registered cameras that don't fall into Not Calibrated or Calibrating)
  // This catches any registered cameras not already filtered by the above conditions
  if (selectedStatuses.includes(DEVICE_STATUS_DISPLAY.PENDING)) {
    if (rawStatus === DEVICE_STATUS.REGISTERED) {
      // Only return true if not already matched by Not Calibrated or Calibrating
      const isNotCalibrated = calibration === undefined || calibrationStatus == null || calibrationStatus < 0;
      const isCalibrating = calibrationStatus != null && calibrationStatus >= 0 && calibrationStatus < 100;
      
      if (!isNotCalibrated && !isCalibrating && calibrationStatus !== 100) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Apply generic status filter (used for companion devices)
 */
export const applyGenericStatusFilter = (record, selectedOptions) => {
  const isPendingDevice = !record.TagIds || (Array.isArray(record.TagIds) && record.TagIds.length === 0);
  
  // Check for Pending status - devices without TagIds
  if (
    selectedOptions[FILTER_KEYS.STATUS].includes(DEVICE_STATUS_DISPLAY.PENDING) &&
    isPendingDevice
  ) {
    return true;
  }
  
  // Check for Online status - only onboarded devices (with TagIds) that are online
  if (
    selectedOptions[FILTER_KEYS.STATUS].includes(DEVICE_STATUS_DISPLAY.ONLINE) &&
    !isPendingDevice &&
    (record[FILTER_KEYS.STATUS] === DEVICE_STATUS.READY ||
      record[FILTER_KEYS.STATUS]?.toLowerCase() === DEVICE_STATUS.ONLINE)
  ) {
    return true;
  }
  
  // Check for Offline status - only onboarded devices (with TagIds) that are offline
  if (
    selectedOptions[FILTER_KEYS.STATUS].includes(DEVICE_STATUS_DISPLAY.OFFLINE) &&
    !isPendingDevice &&
    record[FILTER_KEYS.STATUS]?.toLowerCase() === DEVICE_STATUS.OFFLINE
  ) {
    return true;
  }
  
  return false;
};

/**
 * Apply pending invites role filter
 */
export const applyPendingInvitesRoleFilter = (record, values) => {
  if (!Array.isArray(values) || values.length === 0) return true;
  return record?.role?.id && values.includes(record.role.id);
};

/**
 * Apply pending invites site filter
 */
export const applyPendingInvitesSiteFilter = (record, values) => {
  if (!record || !Array.isArray(values)) {
    return false;
  }
  if (record.siteIds && Array.isArray(record.siteIds)) {
    return record.siteIds.some((siteId) => values.includes(siteId));
  } else if (record.site?.id) {
    return values.includes(record.site.id);
  }
  return false;
};

/**
 * Handle location filter changes for hierarchical location selection
 */
export const handleLocationFilterChange = (
  prevSelections,
  tab,
  option,
  pageIdentifier
) => {
  if (
    tab === FILTER_KEYS.LOCATION &&
    (pageIdentifier === PAGE_IDENTIFIERS.CAMERAS_TAB ||
      pageIdentifier === PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB ||
      pageIdentifier === PAGE_IDENTIFIERS.COMPANION_TAB ||
      pageIdentifier === PAGE_IDENTIFIERS.ALERTS)
  ) {
    const current = Array.isArray(prevSelections[tab])
      ? [...prevSelections[tab]]
      : [];
    const value = option.value;
    const [newType, newValue] = value.split("_");
    const isSelected = current.includes(value);

    if (isSelected) {
      // Remove the specific value
      return {
        ...prevSelections,
        [tab]: current.filter((item) => item !== value),
      };
    }

    // Handle hierarchy logic
    if (value.startsWith(LOCATION_PREFIXES.SITE)) {
      // Changing site invalidates previously selected floor / area
      const filtered = current.filter(
        (tok) =>
          !tok.startsWith(LOCATION_PREFIXES.SITE) &&
          !tok.startsWith(LOCATION_PREFIXES.FLOOR) &&
          !tok.startsWith(LOCATION_PREFIXES.AREA)
      );
      // If the site value is empty (i.e. "All(default)" was selected), just clear the site/floor/area tokens
      const siteId = value.slice(LOCATION_PREFIXES.SITE.length);
      if (!siteId) {
        return { ...prevSelections, [tab]: filtered };
      }
      return { ...prevSelections, [tab]: [...filtered, value] };
    } else if (value.startsWith(LOCATION_PREFIXES.FLOOR)) {
      // Replace floor, drop prior floors & areas
      const filtered = current.filter(
        (tok) =>
          !tok.startsWith(LOCATION_PREFIXES.FLOOR) &&
          !tok.startsWith(LOCATION_PREFIXES.AREA)
      );
      return { ...prevSelections, [tab]: [...filtered, value] };
    } else if (value.startsWith(LOCATION_PREFIXES.AREA)) {
      // Toggle area selection (allow selecting / deselecting single area)
      const hasArea = current.some((tok) => tok === value);
      const withoutAreas = current.filter(
        (tok) => !tok.startsWith(LOCATION_PREFIXES.AREA)
      );
      return {
        ...prevSelections,
        [tab]: hasArea ? withoutAreas : [...withoutAreas, value],
      };
    }
  }

  return null; // Not a location filter
};

// Shared function to apply all filters to data
export const applyAllFilters = (data, filterOptions, pageIdentifier) => {
  if (!data || !Array.isArray(data) || !filterOptions) {
    return data || [];
  }
  
  const isPendingUsersTable = pageIdentifier === PAGE_IDENTIFIERS.PENDING_INVITES;
  
  return data.filter((record) => {
    if (!record) return false;
    
    return Object.keys(filterOptions).every((key) => {
      const values = filterOptions[key];
      if (!values || !Array.isArray(values) || values?.length === 0) {
        return true;
      }
      if (!record[key] && pageIdentifier !== PAGE_IDENTIFIERS.USER_TABLE && !isPendingUsersTable) {
        return false;
      } else if (values.length && record[key]) {
        // Camera status filter
        if ((pageIdentifier === PAGE_IDENTIFIERS.CAMERAS_TAB || pageIdentifier === PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB) && key === FILTER_KEYS.STATUS) {
          return applyCameraStatusFilter(record, filterOptions);
        }
        // Generic status filter
        else if (key === FILTER_KEYS.STATUS) {
          return applyGenericStatusFilter(record, filterOptions);
        }
        // Streams filter
        else if (key === FILTER_KEYS.STREAMS) {
          return record[key].some((item) => values.includes(item));
        }
        // Pending invites role filter
        else if (isPendingUsersTable && key === FILTER_KEYS.ROLE) {
          return applyPendingInvitesRoleFilter(record, values);
        }
        // Pending invites site filter
        else if (isPendingUsersTable && key === FILTER_KEYS.SITE) {
          return applyPendingInvitesSiteFilter(record, values);
        }
        // Camera location filter
        else if ((pageIdentifier === PAGE_IDENTIFIERS.CAMERAS_TAB || pageIdentifier === PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB) && key === FILTER_KEYS.LOCATION) {
          return applyCameraLocationFilter(record, filterOptions);
        }
        // Companion location filter
        else if (pageIdentifier === PAGE_IDENTIFIERS.COMPANION_TAB && key === FILTER_KEYS.LOCATION) {
          return applyCompanionLocationFilter(record, filterOptions);
        }
        // Alerts location filter
        else if (pageIdentifier === PAGE_IDENTIFIERS.ALERTS && key === FILTER_KEYS.LOCATION) {
          return applyAlertsLocationFilter(record, filterOptions);
        }
        // Simple filter - direct match
        else if (filterOptions[key].includes(record[key])) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    });
  });
};


